import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  MapPin, Calendar, Award, Book } from 'lucide-react';

const UniversityOfMichigan: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Michigan";

    return (
        <div className="min-h-screen bg-white font-sans text-[#00274C] selection:bg-[#FFCB05] selection:text-[#00274C]">
            {/* Top Bar - Maize bg */}
            <div className="bg-[#FFCB05] text-[#00274C] py-2 px-4 md:px-8 flex justify-end gap-6 text-xs font-bold uppercase tracking-wide">
                <a href="#" className="hover:underline">Schools & Colleges</a>
                <a href="#" className="hover:underline">Directory</a>
                <a href="#" className="hover:underline">Maps</a>
                <a href="#" className="hover:underline">Give</a>
            </div>

            {/* Nav */}
            <nav className="bg-[#00274C] py-5 px-4 md:px-8 shadow-md">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center text-white">
                    <div className="flex items-center gap-2">
                        <span className="bg-[#FFCB05] text-[#00274C] font-serif font-black text-2xl w-10 h-10 flex items-center justify-center rounded-sm">M</span>
                        <div className="flex flex-col leading-none">
                             <span className="font-serif text-2xl font-bold tracking-wide uppercase text-[#FFCB05]">{schoolName}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-sm tracking-widest uppercase">
                        <a href="#" className="hover:text-[#FFCB05] transition-colors">About</a>
                        <a href="#" className="hover:text-[#FFCB05] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#FFCB05] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#FFCB05] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#FFCB05] transition-colors">Athletics</a>
                    </div>

                    <div className="flex gap-4">
                        <Search className="cursor-pointer hover:text-[#FFCB05]" size={24} />
                        <Menu className="cursor-pointer lg:hidden hover:text-[#FFCB05]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Go Blue */}
            <header className="relative h-[680px]">
                <img 
                    src="https://images.unsplash.com/photo-1590549045330-9b48f938222b?q=80&w=2070&auto=format&fit=crop" 
                    alt="Michigan Law Quad" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#00274C]/90 via-transparent to-transparent"></div>
                
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-xl text-white">
                         <span className="bg-[#FFCB05] text-[#00274C] font-bold text-xs uppercase px-2 py-1 mb-6 inline-block tracking-widest">Leaders and Best</span>
                         <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-none font-serif">
                             Challenge the Present.
                         </h1>
                         <p className="text-xl mb-12 font-light text-gray-200 leading-relaxed">
                             Home to 19 schools and colleges, U-M is one of the world's preeminent research universities.
                         </p>
                         <Button className="bg-[#FFCB05] hover:bg-[#e6b600] text-[#00274C] font-bold uppercase tracking-widest px-10 h-14 rounded-none text-sm">
                             Explore {schoolName.split(' ').pop()}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Quick Stats - Grid */}
            <section className="bg-white py-20 px-4 md:px-8">
                <div className="max-w-[1240px] mx-auto text-center">
                    <h2 className="text-4xl font-bold text-[#00274C] mb-16 font-serif">By the Numbers</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div>
                             <div className="text-5xl font-black text-[#FFCB05] mb-2 font-serif">#1</div>
                             <p className="font-bold text-[#00274C] uppercase text-xs tracking-widest">Public Research University</p>
                        </div>
                        <div>
                             <div className="text-5xl font-black text-[#FFCB05] mb-2 font-serif">100+</div>
                             <p className="font-bold text-[#00274C] uppercase text-xs tracking-widest">Top 10 Grad Programs</p>
                        </div>
                         <div>
                             <div className="text-5xl font-black text-[#FFCB05] mb-2 font-serif">650k</div>
                             <p className="font-bold text-[#00274C] uppercase text-xs tracking-widest">Global Alumni Network</p>
                        </div>
                         <div>
                             <div className="text-5xl font-black text-[#FFCB05] mb-2 font-serif">200+</div>
                             <p className="font-bold text-[#00274C] uppercase text-xs tracking-widest">Years of Excellence</p>
                        </div>
                    </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-[#f3f3f3] py-20 px-4 md:px-8">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-baseline mb-10">
                        <h2 className="text-3xl font-bold text-[#00274C] font-serif">Currents</h2>
                        <a href="#" className="font-bold text-[#00274C] uppercase text-xs tracking-widest hover:text-[#FFCB05] transition-colors">All News</a>
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {[
                             { title: "Climate change impact on Great Lakes", cat: "Research", img: "https://images.unsplash.com/photo-1464039397837-272275ec96e6?q=80&w=1974&auto=format&fit=crop" },
                             { title: "New Robotics facility opens on North Campus", cat: "Innovation", img: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2070&auto=format&fit=crop" },
                             { title: "Arts initiative brings world-renowned cellist", cat: "Arts", img: "https://images.unsplash.com/photo-1465847899078-b8bd516f9f85?q=80&w=2070&auto=format&fit=crop" }
                         ].map((item, i) => (
                             <div key={i} className="bg-white group cursor-pointer shadow-sm hover:shadow-xl transition-all">
                                 <div className="h-48 overflow-hidden relative">
                                     <img src={item.img} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt={item.title}/>
                                     <span className="absolute top-4 left-4 bg-[#00274C] text-white text-[10px] uppercase font-bold px-2 py-1 tracking-widest">{item.cat}</span>
                                 </div>
                                 <div className="p-8">
                                     <h3 className="text-xl font-bold text-[#00274C] leading-tight group-hover:text-[#ae8b00] transition-colors">{item.title}</h3>
                                     <div className="mt-4 w-8 h-1 bg-[#FFCB05]"></div>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#00274C] text-white py-16 px-4 md:px-8 border-t-4 border-[#FFCB05]">
                 <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 text-sm">
                      <div className="max-w-xs">
                          <div className="flex items-center gap-2 mb-6">
                            <span className="bg-[#FFCB05] text-[#00274C] font-serif font-black text-xl w-8 h-8 flex items-center justify-center rounded-sm">M</span>
                            <span className="font-serif text-xl tracking-wide uppercase font-bold">{schoolName}</span>
                        </div>
                          <p className="text-gray-300 mb-6">
                              500 S. State Street <br/>
                              Ann Arbor, MI 48109 <br/>
                              +1 (734) 764-1817
                          </p>
                          <div className="flex gap-4 font-bold text-[#FFCB05] uppercase text-xs tracking-widest">
                              <a href="#" className="hover:text-white">Contact</a>
                              <a href="#" className="hover:text-white">Visit</a>
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-12 font-bold text-gray-300">
                          <ul className="space-y-4">
                              <li className="text-[#FFCB05] uppercase tracking-widest text-xs mb-2">Connect</li>
                              <li><a href="#" className="hover:text-white">{schoolName.split(' ')[0]} Social Media</a></li>
                              <li><a href="#" className="hover:text-white">{schoolName.split(' ').pop()} News</a></li>
                              <li><a href="#" className="hover:text-white">Events</a></li>
                          </ul>
                          <ul className="space-y-4">
                              <li className="text-[#FFCB05] uppercase tracking-widest text-xs mb-2">Legal</li>
                              <li><a href="#" className="hover:text-white">Accessibility</a></li>
                              <li><a href="#" className="hover:text-white">Nondiscrimination</a></li>
                              <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                          </ul>
                      </div>
                 </div>
                 <div className="max-w-[1240px] mx-auto mt-12 pt-8 border-t border-[#FFCB05]/20 text-center text-xs text-gray-400">
                     © 2026 The Regents of {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default UniversityOfMichigan;
